import type { CompleteEvent, EditorEvent, TunaEditor } from '@tunasong/plugin-lib'
import { Editor, Transforms } from '@tunasong/plugin-lib'
import { ReactEditor } from 'slate-react'
import { useCurrentUser } from '@tunasong/redux'
import type { Comment, CoreElement } from '@tunasong/schemas'
import { UserAvatar } from '@tunasong/ui-lib'
import type { FC, HTMLAttributes } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChatEditor } from '../editor/chat-editor.js'
import { isComposerEmpty } from './composer-empty.js'
import styles from './chat-box.module.css'

export interface ChatBoxProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  onChange: (comment: Comment) => void
  /**
   * Send what was typed. Resolve `false` when the send failed: the box then puts the text back (GH #2453). `retry`
   * submits the same text again, the same way.
   */
  onComplete: (event: EditorEvent, retry: () => Promise<void>) => void | Promise<boolean>
  size?: 'small' | 'medium' | 'large'
  /**
   * One-shot draft to insert. When `seed.seq` changes, the ChatBox clears any
   * current content, inserts `seed.text`, and focuses the input. The parent
   * owns consumption bookkeeping (a new hand-off always carries a new seq), so
   * there is no consumed-callback.
   */
  seed?: { text: string; seq: number }
  /**
   * Stable id forwarded to the underlying editor's root element. Lets siblings
   * (like an empty-state header) wire `aria-describedby` to this ChatBox so
   * screen readers hear the surrounding context.
   */
  id?: string
}

export const ChatBox: FC<ChatBoxProps> = props => {
  const {
    onChange,
    onComplete,
    size = 'tiny',
    seed,
    id,
    ...restProps
  } = props

  const { userId = 'unknown' } = useCurrentUser()
  const chatElement = useMemo(
    () =>
      ({
        type: 'comment',
        userId,
        children: [{ type: 'line', children: [{ text: '' }] }],
      }) satisfies Partial<CoreElement<Comment>>,
    [userId]
  )

  // useState (not useRef) so the seed useEffect re-fires when the editor
  // becomes available. With useRef, a seed set BEFORE the editor mounted
  // (e.g., from the block-affordance dispatch path, which dispatches the
  // hand-off right after opening the sidebar tool) was missed: the effect ran
  // once with editor === null, returned early, and never re-ran because
  // [seed] didn't change again. Tracking the editor in state makes
  // editor-ready a dependency change that retriggers the insert.
  const [editor, setEditor] = useState<TunaEditor | null>(null)
  const handleEditor = useCallback((e: TunaEditor) => {
    setEditor(e)
  }, [])

  /**
   * When `seed.seq` changes (OR when the editor becomes available with a seed
   * already set), replace the editor body with `seed.text` and focus the
   * input. Effect deps include `editor` so the mount-after-seed case
   * retriggers. Guarded by `lastSeedSeq` so the same seq does not re-insert
   * on unrelated re-renders.
   */
  const lastSeedSeq = useRef(0)
  useEffect(() => {
    if (!editor || !seed || seed.text.length === 0 || lastSeedSeq.current === seed.seq) {
      return
    }
    lastSeedSeq.current = seed.seq
    Editor.withoutNormalizing(editor, () => {
      const at = {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      }
      Transforms.delete(editor, { at })
      Transforms.setNodes(editor, { type: 'line', children: [{ text: '' }] })
      Transforms.insertText(editor, seed.text)
    })
    try {
      ReactEditor.focus(editor as unknown as ReactEditor)
    } catch {
      // Focus can throw if the editor is unmounted mid-insert; silent recovery
      // is the right call — the user can still click to focus.
    }
  }, [editor, seed])

  const handleComplete = async (event: EditorEvent): Promise<void> => {
    const { editor } = event.detail as CompleteEvent
    const typed = editor.children

    /** Clear the editor */
    Editor.withoutNormalizing(editor, () => {
      const at = {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      }
      Transforms.delete(editor, { at })
      Transforms.setNodes(editor, { type: 'line', children: [{ text: '' }] })
    })

    const sent = await onComplete(event, () => handleComplete(event))

    /**
     * A failed send used to lose what was typed (GH #2453). Put it back, unless something new was typed meanwhile.
     */
    if (sent === false && isComposerEmpty(editor)) {
      Editor.withoutNormalizing(editor, () => {
        const cleared = editor.children.length
        Transforms.insertNodes(editor, typed, { at: [0] })
        for (let index = 0; index < cleared; index++) {
          Transforms.removeNodes(editor, { at: [typed.length] })
        }
      })
    }
  }
  const fontSize = size === 'medium' ? 16 : size === 'small' ? 14 : 18

  return (
    <div
      className={styles.container}
      id={id}
      {...restProps}
    >
      <UserAvatar size={size} className={styles.avatar} />
      <ChatEditor
        className={styles.editor}
        style={{ fontSize }}
        element={chatElement}
        autoFocus={true}
        onChange={onChange}
        onComplete={handleComplete}
        onEditor={handleEditor}
      />
    </div>
  )
}
